import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Col, Row } from 'antd';
import { CollapseBlock } from '../../../../common/index.jsx';
import { useTranslation } from 'react-i18next';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import { required } from '../../../../antd/form/FormValidation.js';
import FormTextField from '../../../../antd/form/FormTextField/FormTextField.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormButton from '../../../../antd/form/FormButton/FormButton.jsx';
import FormTime from '../../../../antd/form/FormTimePicker/FormTime.jsx';
import FormDate from '../../../../antd/form/FormDate/FormDate.jsx';
import dayjs from 'dayjs';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch.jsx';
import {
  DATETIME_FORMAT_DATE,
  DATETIME_FORMAT_DATETIME,
} from '../../../../../utils/helpers/CommonHelper.jsx';
import Form from '../../../../antd/form/Form/Form.jsx';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';

const ReservationSequenceForm = ({
  roomsOther,
  roomsOfDepartment,
  teachers,
  form,
  collidedSubjects,
  timeConflictsChecked,
  timeConflicts,
  isRoomReservationsAdmin,
  subjects,
  onTermsAvailabilityChange,
  onFinish,
  initialValues,
  currentReservationUserId,
}) => {
  const { t } = useTranslation();
  const { errorMessage: showServerError } = useToastMessages();
  const { getFieldValue } = form;
  const [allDay, setAllDay] = useState(getFieldValue('all_day'));
  const [validTerms, setValidTerms] = useState([]);
  // Controls if we show any feedback (alert or red message) about terms
  const [canShowTermsFeedback, setCanShowTermsFeedback] = useState(false);
  const [noTerms, setNoTerms] = useState(false);

  // Disable past dates
  const disabledDate = (current) => {
    return current.isBefore(dayjs(), 'day'); // Disable all dates before today
  };

  // Improved validator for the "date_to" field
  const validateDateTo = async (_rule) => {
    const fromVal = getFieldValue('date_from');
    const toVal = getFieldValue('date_to');
    if (!fromVal || !toVal) return Promise.resolve();

    const dateFrom = dayjs(fromVal).startOf('day');
    const dateTo = dayjs(toVal).startOf('day');
    if (!dateFrom.isValid() || !dateTo.isValid()) return Promise.resolve();

    if (dateTo.isBefore(dateFrom)) {
      return Promise.reject(
        t('Dátum konca nesmie byť skôr ako dátum začiatku'),
      );
    }
    return Promise.resolve();
  };

  // Validator for the "date_from" field: triggers date_to validation if needed
  const validateDateFrom = async (_rule) => {
    const fromVal = getFieldValue('date_from');
    const toVal = getFieldValue('date_to');
    if (!fromVal || !toVal) return Promise.resolve();

    const dateFrom = dayjs(fromVal).startOf('day');
    const dateTo = dayjs(toVal).startOf('day');
    if (!dateFrom.isValid() || !dateTo.isValid()) return Promise.resolve();

    // If start is after end, trigger validation for date_to
    if (dateFrom.isAfter(dateTo)) {
      // Trigger validation for date_to so error appears there
      setTimeout(() => form.validateFields(['date_to']), 0);
    }
    return Promise.resolve();
  };

  // Helper to normalize time values to dayjs objects
  const normalizeTime = (val) => {
    if (!val) return null;
    if (dayjs.isDayjs(val)) return val;
    // Try to parse as string (e.g. 'HH:mm')
    return dayjs(val, 'HH:mm');
  };

  // Validator for the "time_from" field: triggers time_to validation if needed
  const validateTimeFrom = async (_rule, value) => {
    const from = normalizeTime(value);
    const to = normalizeTime(getFieldValue('time_to'));
    const allDayVal = getFieldValue('all_day');
    if (allDayVal) return Promise.resolve(); // skip validation if all_day is true
    if (!from || !to) return Promise.resolve();
    if (!from.isValid() || !to.isValid()) return Promise.resolve();
    // If from >= to, trigger validation for time_to so error appears there
    if (!to.isAfter(from)) {
      setTimeout(() => form.validateFields(['time_to']), 0);
    }
    // Force update to trigger re-render for alert
    setValidTerms((terms) => [...terms]);
    return Promise.resolve();
  };

  // Validator for the "time_to" field: only validate if allDay is false
  const validateTimeTo = async (_rule, value) => {
    const from = normalizeTime(getFieldValue('time_from'));
    const to = normalizeTime(value);
    const allDayVal = getFieldValue('all_day');
    if (allDayVal) return Promise.resolve(); // skip validation if all_day is true
    if (!from || !to) return Promise.resolve();
    if (!from.isValid() || !to.isValid()) return Promise.resolve();
    if (!to.isAfter(from)) {
      return Promise.reject(t('Koniec musí byť neskôr ako začiatok'));
    }
    // Force update to trigger re-render for alert
    setValidTerms((terms) => [...terms]);
    return Promise.resolve();
  };

  // Helper: get selected weekdays as array of numbers (0=Sunday, 1=Monday, ...)
  const getSelectedWeekdays = () => {
    return [
      form.getFieldValue('on_monday') ? 1 : null,
      form.getFieldValue('on_tuesday') ? 2 : null,
      form.getFieldValue('on_wednesday') ? 3 : null,
      form.getFieldValue('on_thursday') ? 4 : null,
      form.getFieldValue('on_friday') ? 5 : null,
    ].filter((x) => x !== null);
  };

  // Slovak day names (0=Sunday, 1=Monday, ...)
  const skDayNames = [
    t('Nedeľa'),
    t('Pondelok'),
    t('Utorok'),
    t('Streda'),
    t('Štvrtok'),
    t('Piatok'),
    t('Sobota'),
  ];

  // Compute all valid terms (dates with times and day names)
  const computeValidTerms = () => {
    // Default: hide feedback until inputs are valid and complete
    setCanShowTermsFeedback(false);
    setNoTerms(false);

    const notify = (hasAny) => {
      if (typeof onTermsAvailabilityChange === 'function') {
        onTermsAvailabilityChange(Boolean(hasAny));
      }
    };

    // If there is any validation error on time_to, never show alert
    const timeToErrors = form.getFieldError
      ? form.getFieldError('time_to')
      : [];
    if (Array.isArray(timeToErrors) && timeToErrors.length > 0) {
      setValidTerms([]);
      notify(false);
      return;
    }

    // Also block feedback on any date/time field errors
    const hasDateFromErr =
      ((form.getFieldError && form.getFieldError('date_from')) || []).length >
      0;
    const hasDateToErr =
      ((form.getFieldError && form.getFieldError('date_to')) || []).length > 0;
    const hasTimeFromErr =
      ((form.getFieldError && form.getFieldError('time_from')) || []).length >
      0;
    if (hasDateFromErr || hasDateToErr || hasTimeFromErr) {
      setValidTerms([]);
      notify(false);
      return;
    }

    const fromVal = form.getFieldValue('date_from');
    const toVal = form.getFieldValue('date_to');
    let timeFrom = form.getFieldValue('time_from');
    let timeTo = form.getFieldValue('time_to');
    const allDayVal = form.getFieldValue('all_day');

    // Dates must be present and valid
    if (!fromVal || !toVal) {
      setValidTerms([]);
      notify(false);
      return;
    }
    const dateFrom = dayjs(fromVal).startOf('day');
    const dateTo = dayjs(toVal).startOf('day');
    if (!dateFrom.isValid() || !dateTo.isValid()) {
      setValidTerms([]);
      notify(false);
      return;
    }
    if (dateTo.isBefore(dateFrom)) {
      // date range invalid => don't show feedback
      setValidTerms([]);
      notify(false);
      return;
    }

    // When not all-day, both times must exist and be valid and time_to > time_from
    if (!allDayVal) {
      const tf = normalizeTime(timeFrom);
      const tt = normalizeTime(timeTo);
      if (!tf || !tt || !tf.isValid() || !tt.isValid() || !tt.isAfter(tf)) {
        setValidTerms([]);
        notify(false);
        return;
      }
    }

    const weekdays = getSelectedWeekdays();
    if (weekdays.length === 0) {
      // We have valid inputs but no days selected -> show the red message
      setValidTerms([]);
      setCanShowTermsFeedback(true);
      setNoTerms(true);
      notify(false);
      return;
    }

    let current = dateFrom;
    const end = dateTo;
    const found = [];
    // Avoid dependency on isSameOrBefore plugin
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      if (weekdays.includes(current.day())) {
        let timeStr = '';
        if (!allDayVal && timeFrom && timeTo) {
          const tf = dayjs.isDayjs(timeFrom)
            ? timeFrom
            : dayjs(timeFrom, 'HH:mm');
          const tt = dayjs.isDayjs(timeTo) ? timeTo : dayjs(timeTo, 'HH:mm');
          if (tf.isValid() && tt.isValid()) {
            timeStr = ` ${tf.format('HH:mm')} - ${tt.format('HH:mm')}`;
          }
        }
        found.push(
          `${skDayNames[current.day()]} ${current.format('DD.MM.YYYY')}${timeStr}`,
        );
      }
      current = current.add(1, 'day');
    }

    setValidTerms(found);
    setCanShowTermsFeedback(true);
    setNoTerms(found.length === 0);
    notify(found.length > 0);
  };

  // Call computeValidTerms on all relevant changes
  useEffect(() => {
    computeValidTerms();
  }, [
    form.getFieldValue('date_from'),
    form.getFieldValue('date_to'),
    form.getFieldValue('on_monday'),
    form.getFieldValue('on_tuesday'),
    form.getFieldValue('on_wednesday'),
    form.getFieldValue('on_thursday'),
    form.getFieldValue('on_friday'),
    form.getFieldValue('all_day'),
    form.getFieldValue('time_from'),
    form.getFieldValue('time_to'),
  ]);

  // When all_day is turned off, validate time fields after they mount
  useEffect(() => {
    if (!allDay) {
      setTimeout(() => form.validateFields(['time_from', 'time_to']), 0);
    }
  }, [allDay]);

  useEffect(() => {
    if (currentReservationUserId && !form.getFieldValue('created_for_id')) {
      form.setFieldValue('created_for_id', currentReservationUserId);
    }
  }, [currentReservationUserId, form]);

  // Intercept submit: if the red "no terms" message is active, show a toast with the same text
  const handleFinish = (values) => {
    if (canShowTermsFeedback && noTerms) {
      showServerError({
        message: t(
          'Nie je možné vytvoriť žiadny termín v zadanom intervale podľa zadaných parametrov.',
        ),
      });
      return; // prevent submit when no terms can be constructed
    }
    // Prevent submit when conflicts are detected
    if (
      Array.isArray(timeConflicts?.conflicts) &&
      timeConflicts.conflicts.length > 0
    ) {
      showServerError({
        message: t(
          'Jeden alebo viac z termínov opakovania koliduje s existujúcimi rezerváciami. Upravte výber termínov.',
        ),
      });
      return;
    }
    if (typeof onFinish === 'function') {
      onFinish(values);
    }
  };

  return (
    <Form form={form} onFinish={handleFinish} initialValues={initialValues}>
      <CollapseBlock>
        <Row>
          <Col xs={24} md={6}>
            <FormSelect
              label={t('Miestnosť')}
              name={'room_id'}
              options={[
                {
                  label: <span>{t('Katedrové')}</span>,
                  title: 'katedrove',
                  options: roomsOfDepartment,
                },
                {
                  label: <span>{t('Ostatné')}</span>,
                  title: 'ostatne',
                  options: roomsOther,
                },
              ]}
              rules={[required(t)]}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={24} md={6}>
            <FormSelect
              label={t('Rezervované pre')}
              name={'created_for_id'}
              options={teachers}
              rules={[required(t)]}
              initialValue={initialValues?.created_for_id}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={24} md={6}>
            <FormTextField
              label={t('Dôvod rezervácie')}
              name={'reason'}
              rules={[required(t)]}
            />
          </Col>
        </Row>
        <Row>
          <FormSwitch
            name="all_day"
            label={t('Celý deň')}
            checked={allDay}
            onChange={(checked) => {
              setAllDay(checked);
              form.setFieldsValue({ all_day: checked });
              // Always revalidate both time fields when all_day changes
              form.validateFields(['time_from', 'time_to']);
              form.validateFields(['date_to']);
              computeValidTerms();
            }}
          />
        </Row>
        <Row gutter={[10]} style={{ width: '100%' }}>
          <Col xs={24} md={6}>
            <FormDate
              label={t('Začiatok opakovania')}
              name={'date_from'}
              rules={[{ validator: validateDateFrom }, required(t)]}
              disabledDate={disabledDate}
              format={DATETIME_FORMAT_DATE}
              onChange={() => {
                setTimeout(() => form.validateFields(['date_to']), 0);
                computeValidTerms();
              }}
            />
          </Col>
          <Col xs={24} md={6}>
            <FormDate
              label={t('Koniec opakovania')}
              name={'date_to'}
              rules={[{ validator: validateDateTo }, required(t)]}
              showTime={false}
              disabledDate={disabledDate}
              format={DATETIME_FORMAT_DATE}
              onChange={() => {
                computeValidTerms();
              }}
            />
          </Col>
        </Row>

        {!allDay ? (
          <>
            <Row gutter={[10]} style={{ width: '100%' }}>
              <Col xs={24} md={6}>
                <FormTime
                  label={t('V čase od')}
                  name={'time_from'}
                  rules={[{ validator: validateTimeFrom }, required(t)]}
                  onChange={() => {
                    setTimeout(() => form.validateFields(['time_to']), 0);
                    computeValidTerms();
                  }}
                />
              </Col>
              <Col xs={24} md={6}>
                <FormTime
                  label={t('V čase do')}
                  name={'time_to'}
                  rules={[{ validator: validateTimeTo }, required(t)]}
                  onChange={() => {
                    form.validateFields(['time_to']);
                    computeValidTerms();
                  }}
                />
              </Col>
            </Row>
          </>
        ) : (
          ''
        )}

        <Row gutter={[8, 0]}>
          <Col span={3}>
            <FormSwitch
              label={t('Pondelok')}
              name={'on_monday'}
              onChange={computeValidTerms}
            />
          </Col>
          <Col span={3}>
            <FormSwitch
              label={t('Utorok')}
              name={'on_tuesday'}
              onChange={computeValidTerms}
            />
          </Col>
          <Col span={3}>
            <FormSwitch
              label={t('Streda')}
              name={'on_wednesday'}
              onChange={computeValidTerms}
            />
          </Col>
          <Col span={3}>
            <FormSwitch
              label={t('Štvrtok')}
              name={'on_thursday'}
              onChange={computeValidTerms}
            />
          </Col>
          <Col span={3}>
            <FormSwitch
              label={t('Piatok')}
              name={'on_friday'}
              onChange={computeValidTerms}
            />
          </Col>
        </Row>

        {canShowTermsFeedback &&
          (noTerms ? (
            <div style={{ color: 'red', marginBottom: 10 }}>
              {t(
                'Nie je možné zostaviť žiadny termín v zadanom intervale podľa označených dní.',
              )}
            </div>
          ) : (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: '10px' }}
              title={t('Zoznam termínov, ktoré vyhovujú zvoleným parametrom:')}
              description={
                <ul style={{ marginBottom: 0 }}>
                  {validTerms.map((date) => (
                    <li key={date}>{date}</li>
                  ))}
                </ul>
              }
            />
          ))}

        {Array.isArray(timeConflicts?.conflicts) &&
          timeConflicts.conflicts.length > 0 && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: '10px' }}
              title={t(
                'Jeden alebo viac z termínov opakovania koliduje s jednou alebo viacerými rezerváciami:',
              )}
              description={
                <ul style={{ marginBottom: 0 }}>
                  {timeConflicts.conflicts.map((conflict, i) => (
                    <li
                      key={`${conflict.id || i}-${conflict.date_from || conflict.from}-${conflict.date_to || conflict.to}`}
                    >
                      <b>{conflict.orderedByName || ''} </b>(
                      {dayjs(conflict.date_from || conflict.from).format(
                        DATETIME_FORMAT_DATETIME,
                      )}
                      {' - '}
                      {dayjs(conflict.date_to || conflict.to).format(
                        DATETIME_FORMAT_DATETIME,
                      )}
                      )<em>{conflict.reason ? ` - ${conflict.reason}` : ''}</em>
                    </li>
                  ))}
                </ul>
              }
            />
          )}

        {Array.isArray(timeConflicts?.conflicts) &&
          timeConflicts.conflicts.length === 0 &&
          timeConflictsChecked && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: '10px' }}
              title={t('Žiadne konflikty v termínoch')}
              description={t('Miestnosť je v zadaných termínoch voľná.')}
            />
          )}

        <Col xs={24} md={6}>
          <FormSelect
            label={t('Predmet')}
            name={'subject_code'}
            options={subjects}
            help={
              <div style={{ whiteSpace: 'nowrap', marginBottom: '10px' }}>
                {t(
                  'Ak vyberiete predmet, aplikácia zistí ostatné skúšky v zadaných termínoch opakovania pre rovnaký ročník a študijný program a zobrazí kolízie.',
                )}
              </div>
            }
          />
        </Col>

        {getFieldValue('subject_code') !== undefined &&
          (Array.isArray(collidedSubjects) && collidedSubjects.length > 0 ? (
            <Alert
              type="warning"
              title={t(
                'Zoznam rezervácií týkajúcich sa predmetov pre rovnaký ročník a študijný program:',
              )}
              style={{ marginBottom: '10px' }}
              description={
                <ul>
                  {collidedSubjects.map((collision) => {
                    if (collision.room_id !== form.getFieldValue('room_id')) {
                      return (
                        <li
                          key={`${collision.subject_code}-${collision.date_from}-${collision.date_to}`}
                        >
                          <b>{collision.name}</b> ({collision.displayname}{' '}
                          {dayjs(collision.date_from).format(
                            DATETIME_FORMAT_DATETIME,
                          )}
                          -
                          {dayjs(collision.date_to).format(
                            DATETIME_FORMAT_DATETIME,
                          )}
                          )
                        </li>
                      );
                    }
                    return null;
                  })}
                </ul>
              }
            />
          ) : (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: '10px' }}
              title={t('Žiadne kolízie s inými predmetmi')}
              description={t(
                'Neboli nájdené žiadne rezervácie s predmetmi pre rovnaký ročník a študijný program.',
              )}
            />
          ))}

        {isRoomReservationsAdmin ? (
          <FormSwitch name="auto_accept" label={t('Automaticky schváliť')} />
        ) : (
          ''
        )}

        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'reservation-sequences'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </CollapseBlock>
    </Form>
  );
};
ReservationSequenceForm.propTypes = {
  props: PropTypes.any,
  currentReservationUserId: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

export default ReservationSequenceForm;
